import * as React from "react";
import * as Yup from "yup";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import { authLogin, authRegister, authSendEmail } from "@/redux/auth/services";

import { LoadingButton } from "@mui/lab";
import LoginWithSocials from "@/components/normal/login-with-socials";
import { RegisterSteps } from ".";
import { toaster } from "@/components/reusables/toaster";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";

interface IRegisterInputsProps {
  changeStep: (step: RegisterSteps) => void;
  setEmail: (email: string) => void;
}

interface IFormikValues {
  fullName: string;
  email: string;
  password: string;
  agreement: boolean;
}

const RegisterInputs: React.FunctionComponent<IRegisterInputsProps> = ({
  changeStep,
  setEmail,
}) => {
  const { t } = useTranslation(["inputValidations", "common"]);
  const dispatch = useDispatch();
  const [serverError, setServerError] = React.useState("");

  const {
    values,
    isSubmitting,
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setErrors,
  } = useFormik<IFormikValues>({
    validationSchema: Yup.object().shape({
      fullName: Yup.string().required(
        t("inputValidations:this_field_is_required")
      ),
      email: Yup.string()
        .email(t("inputValidations:please_insert_valid_email"))
        .required(t("inputValidations:this_field_is_required")),
      password: Yup.string().required(
        t("inputValidations:this_field_is_required")
      ),
      agreement: Yup.bool().oneOf(
        [true],
        t("inputValidations:you_have_to_agree_with_our_terms")
      ),
    }),
    initialValues: {
      fullName: "",
      email: "",
      password: "",
      agreement: false,
    },
    onSubmit: (values, actions) => {
      actions.setSubmitting(true);
      dispatch(
        authRegister(
          values,
          actions.setSubmitting,
          ({ data }) => {
            changeStep(RegisterSteps.CheckEmail);
            setEmail(values.email);
            dispatch(
              authLogin(
                {
                  email: values.email,
                  password: values.password,
                },
                undefined,
                (res) => {
                  setTimeout(() => {
                    dispatch(authSendEmail(() => {}));
                  }, 100);
                },
                (err) => {
                  //Show error
                  // toaster.warning(t("common:something_went_wrong"))
                  toaster.warning(err.message);
                }
              )
            );
          },
          (err) => {
            console.log(err);
            setServerError(err.message);
            //Show error
            // toaster.warning(t("common:something_went_wrong"))
            toaster.warning(err.message);
          }
        )
      );
    },
  });

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        onChange={() => setServerError("")}
      >
        <Typography component="h1" variant="h6" sx={{ mb: 2 }}>
          {t("common:sign_up")}
        </Typography>
        <Stack direction="column" spacing={2}>
          <TextField
            {...getFieldProps("fullName")}
            label={t("common:full_name")}
            error={Boolean(touched.fullName && errors.fullName)}
            helperText={touched.fullName && errors.fullName}
          />
          <TextField
            {...getFieldProps("email")}
            label={t("common:email")}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />
          <TextField
            {...getFieldProps("password")}
            label={t("common:password")}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            type="password"
          />
          <FormGroup>
            <FormControlLabel
              control={<Checkbox {...getFieldProps("agreement")} />}
              label={
                <span>
                  {t("common:i_agree_to_aren_pro")}{" "}
                  <Link
                    href="https://www.arenpro.com/privacy-policy"
                    target="_blank"
                  >
                    {t("common:terms")}
                  </Link>{" "}
                  {t("common:and")}{" "}
                  <Link
                    href="https://www.arenpro.com/terms-condition"
                    target="_blank"
                  >
                    {t("common:terms_condition")}
                  </Link>
                </span>
              }
            />

            {Boolean(touched.agreement && errors.agreement) && (
              <FormHelperText error={true}>{errors.agreement}</FormHelperText>
            )}
          </FormGroup>
        </Stack>
        {serverError && <Alert severity="error">{serverError}</Alert>}
        <LoadingButton
          sx={{ mt: 8 }}
          fullWidth
          color="primary"
          variant="contained"
          type="submit"
          loading={isSubmitting}
        >
          {t("common:get_started_now")}
        </LoadingButton>
      </Box>
      <Typography component="span" sx={{ display: "block", my: 2 }}>
        {t("common:or_user_email_to_sign_in")}
      </Typography>
      <LoginWithSocials />
    </>
  );
};

export default RegisterInputs;
